/**
 * ReadinessBar — horizontal AI Readiness Level progress bar with level label,
 * large percentage, and XP range labels.
 */

import React from 'react';

interface ReadinessBarProps {
  level: string;
  pct: number;
  xpMin?: number;
  xpMax?: number;
  showViewDetails?: boolean;
}

export const ReadinessBar: React.FC<ReadinessBarProps> = ({
  level,
  pct,
  xpMin = 0,
  xpMax = 100,
  showViewDetails = false,
}) => {
  const clamped = Math.min(100, Math.max(0, Math.round(pct)));

  return (
    <div
      className="rounded-2xl p-5 bg-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
      style={{ border: '1.5px solid #e8e8ee', cursor: showViewDetails ? 'pointer' : 'default' }}
    >
      <p className="font-montserrat font-bold text-sm text-neutral-800 mb-1">
        AI Readiness Level
      </p>
      <p className="font-open-sans text-sm text-neutral-600 mb-3">{level}</p>

      {/* Large percentage */}
      <div className="flex items-baseline gap-2 mb-3">
        <span
          className="font-montserrat font-extrabold text-4xl leading-none"
          style={{ color: '#5b6cff' }}
        >
          {clamped}%
        </span>
        <span className="font-open-sans text-xs text-neutral-500">to next milestone</span>
      </div>

      {/* Bar */}
      <div className="h-3 rounded-full overflow-hidden mb-2" style={{ background: '#e8e8ee' }}>
        <div
          className="h-full rounded-full"
          style={{
            width: `${clamped}%`,
            background: 'linear-gradient(90deg, #5b6cff, #00c2a8)',
            transition: 'width 0.8s ease',
          }}
        />
      </div>

      {/* XP labels */}
      <div className="flex justify-between">
        <span className="font-open-sans text-xs text-neutral-400">{xpMin} XP</span>
        <span className="font-open-sans text-xs text-neutral-400">{xpMax} XP</span>
      </div>

      {/* View details affordance */}
      {showViewDetails && (
        <p
          className="font-open-sans text-xs mt-3 text-right"
          style={{ color: '#9ca3af' }}
        >
          View details →
        </p>
      )}
    </div>
  );
};
